import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

const STORAGE_KEY = 'rankonai-auth';
const LEGACY_STORAGE_KEY = 'geo-saas-auth';

// One-time migration from the pre-rebrand key. Runs at module load; if the new
// key is empty and the old one has data, we copy it across and delete the
// legacy entry so users don't get logged out by the rename.
if (typeof window !== 'undefined') {
  for (const store of [window.localStorage, window.sessionStorage]) {
    if (!store.getItem(STORAGE_KEY) && store.getItem(LEGACY_STORAGE_KEY)) {
      store.setItem(STORAGE_KEY, store.getItem(LEGACY_STORAGE_KEY));
      store.removeItem(LEGACY_STORAGE_KEY);
    }
  }
}

// Picks localStorage (remembered) or sessionStorage (this-tab-only) based on
// the `remember` flag at write time. Reads check both so an existing session
// is found regardless of which one wrote it.
const rememberMeStorage = {
  getItem: (name) => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(name) ?? window.sessionStorage.getItem(name);
  },
  setItem: (name, value) => {
    if (typeof window === 'undefined') return;
    let remember = true;
    try {
      remember = JSON.parse(value)?.state?.remember !== false;
    } catch {
      // fall through to default true
    }
    if (remember) {
      window.localStorage.setItem(name, value);
      window.sessionStorage.removeItem(name);
    } else {
      window.sessionStorage.setItem(name, value);
      window.localStorage.removeItem(name);
    }
  },
  removeItem: (name) => {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(name);
    window.sessionStorage.removeItem(name);
  },
};

export const useAuthStore = create(
  persist(
    (set) => ({
      token: null,
      user: null,
      remember: true,
      login: (token, user, remember = true) => set({ token, user, remember }),
      logout: () => set({ token: null, user: null }),
      setUser: (user) => set({ user }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => rememberMeStorage),
    },
  ),
);
